import React from "react";
import { Button } from "@/components/ui/button";
import { UserCheck, Pencil } from "lucide-react";
import { UserAvatar } from "@/components/ui/user-avatar";
import { TravelRequestBadge } from "./TravelRequestBadge";
import { TravelRequestButton, canAssignToAdvisor, getAssignToAdvisorLabel } from "./TravelRequestButton";
import { UserRole } from "@/lib/auth/models/auth.model";
import { useI18n } from "@/hooks/useI18n";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn, formatBudget, formatTravelDates } from "@/components/utils/utils";
import { DashboardTravelRequest } from "@/lib/models/dashboard";

interface TravelRequestsTableProps {
  travelRequest: DashboardTravelRequest[];
  currentPage: number;
  itemsPerPage: number;
  userRole: UserRole | null;
  onPageChange: (page: number) => void;
  onViewRequest: (requestId: string) => void;
  onCreateItinerary?: (requestId: string) => void;
  /** Solo agency: gira la richiesta a un travel advisor membro */
  onAssignToMember?: (requestId: string) => void;
  /** Apre il modale della richiesta in modifica. Assente = azione non disponibile. */
  onEditRequest?: (requestId: string) => void;
  /** Riga per riga: se la modifica e' concessa. Assente = concessa ovunque. */
  canEditRequest?: (travelRequest: DashboardTravelRequest) => boolean;
  /**
   * Solo agency: la richiesta non e' su nessun membro (vedi `needsAgencyAssignment`).
   * L'azione di modifica diventa "Assegna" col pallino rosso, come nelle card.
   */
  needsAssignment?: (travelRequest: DashboardTravelRequest) => boolean;
  hideActions?: boolean;
  hideExtraInfo?: boolean;
  onRowClick?: (requestId: string) => void;
  selectedRowId?: string | null;
}

export const TravelRequestsTable: React.FC<TravelRequestsTableProps> = ({
  travelRequest,
  currentPage,
  itemsPerPage,
  userRole,
  onPageChange,
  onViewRequest,
  onCreateItinerary,
  onAssignToMember,
  onEditRequest,
  canEditRequest,
  needsAssignment,
  hideActions = false,
  hideExtraInfo = false,
  onRowClick,
  selectedRowId = null,
}) => {
  const { t } = useI18n();
  const showAdvisorColumn = userRole === "admin" || userRole === "agency";
  // Agency e admin possono girare la richiesta a un advisor anche quando la colonna
  // azioni è nascosta (dashboard): in quel caso la colonna resta, con la sola assegnazione.
  const canAssignToMember =
    (userRole === "agency" || userRole === "admin") && !!onAssignToMember;
  const showActionsColumn = !hideActions || canAssignToMember || !!onEditRequest;

  // Logica paginazione
  const totalPages = Math.ceil(travelRequest.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedtravelRequest = travelRequest.slice(startIndex, endIndex);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <div className="min-w-[400px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('travelRequest.tableDestination')}</TableHead>
                <TableHead>{t('travelRequest.tableDates')}</TableHead>
                {!hideExtraInfo && <TableHead>{t('travelRequest.tablePeople')}</TableHead>}
                {!hideExtraInfo && <TableHead>{t('travelRequest.budget')}</TableHead>}
                {!hideExtraInfo && <TableHead>{t('travelRequest.tableStatus')}</TableHead>}
                <TableHead>{t('travelRequest.tableContact')}</TableHead>
                {showAdvisorColumn && <TableHead>{t('travelRequest.designer')}</TableHead>}
                {showActionsColumn && <TableHead>{t('common.actions')}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedtravelRequest.map((trip) => (
                <TableRow 
                  key={trip.id}
                  className={cn(
                    onRowClick && "cursor-pointer hover:bg-gray-50",
                    selectedRowId === trip.id && "bg-blue-50 hover:bg-blue-100"
                  )}
                  onClick={() => onRowClick?.(trip.id)}
                >
                  <TableCell>
                    <div className="font-medium text-gray-900">
                      {trip.destinations.length > 0 ? trip.destinations.join(", ") : "Destinazione da definire"}
                    </div>
                    {trip.departurePoint && (
                      <div className="text-gray-500">Da: {trip.departurePoint}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-gray-600">
                    {formatTravelDates(trip.travelStartDate, trip.travelEndDate, { fallbackText: "Date da definire" })}
                  </TableCell>
                  {!hideExtraInfo && (
                    <TableCell className="text-gray-600">
                      {trip.totalGuests || "N/A"}
                    </TableCell>
                  )}
                  {!hideExtraInfo && (
                    <TableCell className="text-gray-600">
                      {formatBudget(trip.budgetMin, trip.budgetMax)}
                    </TableCell>
                  )}
                  {!hideExtraInfo && (
                    <TableCell>
                      <TravelRequestBadge travelRequest={trip} variant="table" />
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <UserAvatar
                        firstName={trip.contact.firstName}
                        lastName={trip.contact.lastName}
                        email={trip.contactEmail}
                        imageUrl={trip.contact.avatarUrl}
                        size="md"
                      />
                      <div>
                        <div className="font-medium text-gray-900">
                          {trip.contactFullName}
                        </div>
                        <div className="text-gray-500">{trip.contactEmail}</div>
                      </div>
                    </div>
                  </TableCell>
                  {showAdvisorColumn && (
                    <TableCell>
                      {trip.assignedAdvisor ? (
                        <div className="flex items-center gap-2">
                          <UserAvatar
                            firstName={trip.assignedAdvisor.firstName}
                            lastName={trip.assignedAdvisor.lastName}
                            email={trip.assignedAdvisor.email}
                            imageUrl={trip.assignedAdvisor.avatarUrl}
                            size="sm"
                          />
                          <div>
                            <div className="font-medium text-gray-900">{trip.assignedAdvisor.fullName}</div>
                            <div className="text-sm text-gray-500">{trip.assignedAdvisor.email}</div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">{t('travelRequest.notAssigned')}</span>
                      )}
                    </TableCell>
                  )}
                  {showActionsColumn && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                      {onEditRequest && (!canEditRequest || canEditRequest(trip)) && (() => {
                        const toAssign = !!needsAssignment?.(trip);
                        const label = String(toAssign ? t('travelRequest.assign') : t('travelRequest.editRequest'));
                        return (
                          <div className="relative">
                            <Button
                              variant="outline"
                              size="icon"
                              className={cn(
                                "h-8 w-8",
                                toAssign && "border-red-300 text-red-600 hover:bg-red-50",
                              )}
                              onClick={() => onEditRequest(trip.id)}
                              title={label}
                              aria-label={label}
                            >
                              {toAssign ? <UserCheck /> : <Pencil />}
                            </Button>
                            {toAssign && (
                              <span
                                className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"
                                aria-hidden
                              />
                            )}
                          </div>
                        );
                      })()}
                      {hideActions ? (
                        canAssignToMember && canAssignToAdvisor(trip, userRole) ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-sm"
                            onClick={() => onAssignToMember?.(trip.id)}
                          >
                            <UserCheck className="h-3 w-3 mr-1" />
                            {getAssignToAdvisorLabel(trip, userRole, t)}
                          </Button>
                        ) : null
                      ) : (
                        <TravelRequestButton
                          travelRequest={trip}
                          userRole={userRole}
                          onClick={onViewRequest}
                          onCreateItinerary={onCreateItinerary}
                          onAssignToMember={onAssignToMember}
                          variant="table"
                        />
                      )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Paginatore */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center">
          <div className="text-gray-600">
            Mostrando {startIndex + 1}-{Math.min(endIndex, travelRequest.length)} di {travelRequest.length} risultati
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => onPageChange(currentPage - 1)}
                  className={cn(
                    "cursor-pointer",
                    currentPage === 1 && "pointer-events-none opacity-50"
                  )}
                />
              </PaginationItem>

              {/* Pagine numeriche */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Mostra solo alcune pagine per evitare overflow
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => onPageChange(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                } else if (
                  page === currentPage - 2 ||
                  page === currentPage + 2
                ) {
                  return (
                    <PaginationItem key={page}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }
                return null;
              })}

              <PaginationItem>
                <PaginationNext
                  onClick={() => onPageChange(currentPage + 1)}
                  className={cn(
                    "cursor-pointer",
                    currentPage === totalPages && "pointer-events-none opacity-50"
                  )}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};
